import { motion } from 'framer-motion';
import data from '../data/data.json';

const CARDS = [
  {
    key: 'building',
    title: 'Things I\'m Building',
    subtitle: 'Active prototypes & hardware',
    icon: '⚡',
    rotate: -1.2,
    bg: 'linear-gradient(135deg, rgba(255, 253, 245, 0.95) 0%, rgba(254, 243, 235, 0.95) 100%)',
    border: 'rgba(232, 149, 109, 0.35)',
    accent: '#c97348',
  },
  {
    key: 'learning',
    title: 'Things I\'m Learning',
    subtitle: 'Health-AI & signal processing',
    icon: '🔬',
    rotate: 1.4,
    bg: 'linear-gradient(135deg, rgba(246, 253, 248, 0.95) 0%, rgba(235, 248, 240, 0.95) 100%)',
    border: 'rgba(58, 112, 80, 0.3)',
    accent: '#3a7050',
  },
  {
    key: 'curious',
    title: 'Things I\'m Curious About',
    subtitle: 'Emerging questions in health',
    icon: '💡',
    rotate: -0.8,
    bg: 'linear-gradient(135deg, rgba(250, 248, 255, 0.95) 0%, rgba(243, 238, 255, 0.95) 100%)',
    border: 'rgba(80, 64, 160, 0.25)',
    accent: '#5040a0',
  },
];

export default function Currently() {
  const { currently } = data;

  return (
    <section
      id="currently"
      className="section"
      style={{
        background: 'linear-gradient(180deg, rgba(250,246,238,0.3) 0%, rgba(247,190,161,0.08) 50%, rgba(250,246,238,0.3) 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem', marginBottom: '3.5rem' }}>
          <div>
            <motion.p
              className="section-label"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Real-time focus
            </motion.p>
            <motion.h2
              className="section-title foil-shine"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{ marginBottom: 0 }}
            >
              Laboratory Notes
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.6rem',
              padding: '0.45rem 1rem',
              borderRadius: '100px',
              background: 'rgba(255, 255, 255, 0.75)',
              backdropFilter: 'blur(8px)',
              border: '1px solid var(--border)',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: 'var(--text)',
            }}
          >
            <span
              style={{
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                background: '#3a7050',
                boxShadow: '0 0 8px #3a7050',
                animation: 'pulse-soft 2s infinite',
              }}
            />
            <span>Active Sprint & Research</span>
          </motion.div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '2.5rem',
            alignItems: 'stretch',
          }}
        >
          {CARDS.map((card, ci) => (
            <motion.div
              key={card.key}
              initial={{ opacity: 0, y: 36, rotate: 0 }}
              whileInView={{ opacity: 1, y: 0, rotate: card.rotate }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, delay: ci * 0.12, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ rotate: 0, y: -6, transition: { duration: 0.25 } }}
              style={{
                background: card.bg,
                border: `1px solid ${card.border}`,
                borderRadius: '24px',
                padding: '2.2rem 1.8rem',
                boxShadow: '0 12px 32px rgba(45, 23, 83, 0.06)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Modern Minimalist Glass / Metallic Pin */}
              <div
                style={{
                  position: 'absolute',
                  top: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '40px',
                  height: '14px',
                  borderRadius: '4px',
                  background: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(6px)',
                  border: '1px solid rgba(45, 23, 83, 0.12)',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                }}
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1.25rem' }}>
                <span style={{ fontSize: '1.6rem' }}>{card.icon}</span>
                <div>
                  <h3
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontSize: '1.15rem',
                      fontWeight: 600,
                      color: card.accent,
                      lineHeight: 1.2,
                    }}
                  >
                    {card.title}
                  </h3>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {card.subtitle}
                  </span>
                </div>
              </div>

              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '1rem', flexGrow: 1 }}>
                {currently[card.key].map((item, i) => (
                  <li
                    key={i}
                    style={{
                      display: 'flex',
                      gap: '0.65rem',
                      alignItems: 'flex-start',
                      padding: '0.7rem 0.85rem',
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.5)',
                      border: '1px solid rgba(45, 23, 83, 0.04)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#ffffff';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(45, 23, 83, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <span style={{ color: card.accent, marginTop: '0.15rem', fontSize: '0.85rem', flexShrink: 0 }}>
                      ✦
                    </span>
                    <span style={{ fontSize: '0.88rem', color: 'var(--text)', lineHeight: 1.6 }}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
